import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { getV2Portfolios,listPublicPortfolios, updatePortfolioDisplayMode, getV2PublicAdminPortfolios, createV2BulkTransactions, getAllPortfoliosTwo,listAdminPortfolios } from '../composables/fetchAPI'; // หรือ path ที่คุณเก็บ API
import toast from 'react-hot-toast';
import SellBuyStockModal from '../components/PortfolioV2/SellBuyStockModal'; // <-- Import Modal ของคุณเข้ามา
import { manageV2CashTransaction } from '../composables/fetchAPI'; // ✅ อย่าลืม Import
import CryptoTradeModal from '../components/PortfolioV2/CryptoTradeModal';
import { isCryptoPortfolio, normalizeCryptoPortfolio } from '../utils/portfolioType';
// 1. เพิ่ม Type สำหรับคุม Modal
interface PortfolioContextType {
  userPortfolios: any[];
  adminPortfolios: any[];
  loading: boolean;
  refreshUserPortfolios: () => Promise<void>;
  refreshAdminPortfolios: () => Promise<void>;
  refreshShayPortfolios: () => Promise<void>; // ✅ เพิ่มตรงนี้
  shayPortfolios: any[]; // ✅ เพิ่มตรงนี้
  // ✅ เพิ่ม currentPrice เข้าไปตรงนี้
  // พอร์ต CRYPTO จะเปิด CryptoTradeModal แทน
  openTransactionModal: (params?: { portId?: string; subPortId?: string; symbol?: string; currentPrice?: number, initialQuantity?: number; isSellAll?: boolean; }) => void;
  globalRefreshCount: number;
  setDisplayMode: React.Dispatch<React.SetStateAction<'V1' | 'V2'>>;
  displayMode: 'V1' | 'V2';
  toggleDisplayMode: (mode: 'V1' | 'V2') => void;
}

const PortfolioContext = createContext<PortfolioContextType | undefined>(undefined);

export const PortfolioProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [userPortfolios, setUserPortfolios] = useState<any[]>([]);
  const [adminPortfolios, setAdminPortfolios] = useState<any[]>([]);
  const [shayPortfolios, setShayPortfolios] = useState<any[]>([]); // ✅ เพิ่ม State สำหรับพอร์ต Shay
  const [loading, setLoading] = useState<boolean>(true);
  const [globalRefreshCount, setGlobalRefreshCount] = useState(0);
  // ✅ 2. สร้างแม่กุญแจขึ้นมา (ค่าเริ่มต้น false คือยังไม่เคย fetch)
  const isFetched = useRef(false);
  // --- State สำหรับคุม Global Modal ---
  const [isTxModalOpen, setIsTxModalOpen] = useState(false);
  // ✅ สร้าง state สำหรับโหมด (Default เป็น V1)
  const [displayMode, setDisplayMode] = useState<'V1' | 'V2'>(() => {
    const savedMode = localStorage.getItem('portfolioDisplayMode');
    return (savedMode === 'V1' || savedMode === 'V2') ? savedMode : 'V1';
  });

  const toggleDisplayMode = (newMode: 'V1' | 'V2') => {
    setDisplayMode(newMode);
    localStorage.setItem('portfolioDisplayMode', newMode);

    // ไม่ต้องเรียก updatePortfolioDisplayMode (API) แล้ว

    // รีเฟรชข้อมูลพอร์ตใหม่ด้วยโหมดที่เปลี่ยนทันที
    refreshUserPortfolios(newMode);
  };

  const calculateHoldingsFromTransactions = (transactions: any[]) => {
    const map: any = {};
    transactions.forEach((t) => {
      if (!map[t.assetSymbol]) map[t.assetSymbol] = { symbol: t.assetSymbol, quantity: 0, totalCost: 0 };
      if (t.type === 'BUY') {
        map[t.assetSymbol].quantity += t.quantity;
        map[t.assetSymbol].totalCost += (t.quantity * t.price);
      } else if (t.type === 'SELL') {
        map[t.assetSymbol].quantity -= t.quantity;
      }
    });
    return Object.values(map).map((h: any) => ({
      ...h,
      avgCost: h.quantity > 0 ? h.totalCost / h.quantity : 0
    }));
  };

  const normalizePortfolio = (p: any) => {
    const isV2 = !!p.portfolioId; // เช็คจาก field ที่มีเฉพาะ V2
    return {
      ...p,
      portId: p.portId || p.portfolioId,
      name: p.name,
      totalValue: p.totalValue || p.totalMarketValue || 0,
      subPorts: (p.subPorts || p.subPortfolios || []).map((sub: any) => ({
        ...sub,
        subPortId: sub.subPortId || sub.subPortfolioId,
        holdings: isV2 ? calculateHoldingsFromTransactions(sub.transactions || []) : (sub.holdings || []),
        totalValue: sub.totalValue || sub.marketValue || 0
      }))
    };
  };

  const [txModalParams, setTxModalParams] = useState<{
    portId?: string;
    subPortId?: string;
    symbol?: string;
    currentPrice?: number;
    initialQuantity?: number;
    isSellAll?: boolean;
  }>({});
  // 3. ปรับฟังก์ชัน refreshUserPortfolios ให้รับพารามิเตอร์โหมดได้
  const refreshUserPortfolios = async (modeOverride?: 'V1' | 'V2') => {
    try {
      setLoading(true);
      const targetMode = modeOverride || displayMode; // ใช้ค่าที่ส่งมา หรือค่าจาก State

      const res = targetMode === 'V2'
        ? await getV2Portfolios({ summary: false, includeClosed: true })
        : await getAllPortfoliosTwo();

      // พอร์ตคริปโต: ปรับ stockSymbol ของ holding เป็น X:...USD เพื่อให้ดึงราคาคริปโตได้ทุกหน้า
      const data = (res.data?.portfolios || res.portfolios || []).map(normalizeCryptoPortfolio);
      setUserPortfolios(data);
      // ✅ คืนค่าข้อมูลที่เพิ่ง fetch มาด้วย เผื่อผู้เรียกต้องการข้อมูลล่าสุดทันทีโดยไม่ต้องรอ re-render
      return data;
    } catch (error: any) {
      if (error.response && (error.code === 400000 || error.code === 401)) {
        toast.error(error.th_message || error.message || "Failed to fetch portfolios");

        console.error("Failed to fetch portfolios:", error);
      }
    } finally {
      setLoading(false);
    }
  };
// ✅ เพิ่มฟังก์ชันสำหรับดึงข้อมูลพอร์ต Shay
  const refreshShayPortfolios = async () => {
    try {
      // ตัวอย่าง: ถ้ามี API เฉพาะของ Shay หรือดึงจาก endpoint ของแอดมิน/พอร์ตพิเศษ
      // ปรับเปลี่ยน Endpoint ตรงนี้ให้ตรงกับระบบที่คุณใช้จริง (เช่น ดึงจากฟังก์ชันพอร์ตแอดมินหรือฟังก์ชันเฉพาะ)
      const res = await listAdminPortfolios(); 
      const allPortfolios = res.data?.portfolios || [];
      
      // กรองเอาเฉพาะพอร์ตที่เป็นของ Shay (เช่นเช็คจากชื่อพอร์ต)
      const filteredShay = allPortfolios
      setShayPortfolios(filteredShay);
    } catch (error) {
      console.error("Failed to fetch Shay portfolios:", error);
    }
  };
  const refreshAdminPortfolios = async () => {
    try {
      // const res = await getV2PublicAdminPortfolios();
     const res = await listPublicPortfolios();

      setAdminPortfolios(res.data?.portfolios || []);
    } catch (error) {
      console.error("Failed to fetch admin portfolios:", error);
    }
  };
  // ✅ เพิ่มฟังก์ชันปรับเงินสด
  const handleAdjustCash = async (portfolioId: string, amount: number, transactionDate: string) => {
    try {
      // เรียก API ปรับยอดเงิน (CASH_ADJUST) 
      await manageV2CashTransaction(portfolioId, 'CASH_ADJUST', amount, 'ปรับปรุงยอดเงินจากหน้า Popup', transactionDate);
      toast.success('ปรับปรุงยอดเงินสำเร็จ!');

      // 1. ดึงข้อมูลใหม่เพื่อให้อัปเดต UI ใน Context
      await refreshUserPortfolios();

      // ✅ 2. เพิ่มบรรทัดนี้เข้าไป!! เพื่อส่งสัญญาณให้หน้า PortfolioDetailPage รีเฟรชตัวเอง
      setGlobalRefreshCount(prev => prev + 1);

    } catch (error: any) {
      console.error(error);
      toast.error(error.th_message || error.message || 'เกิดข้อผิดพลาดในการปรับยอดเงิน');
      throw error; // โยน error กลับไปให้ Modal ดักจับ
    }
  };

  useEffect(() => {
    refreshUserPortfolios();
    refreshShayPortfolios()
  }, [displayMode]);

  // ✅ 3. เอาแม่กุญแจมาดักใน useEffect
  useEffect(() => {
    // ถ้าเคย fetch ไปแล้ว (กุญแจถูกล็อก) ให้หยุดการทำงานทันที
    if (isFetched.current) return;

    // ล็อกกุญแจทันทีเพื่อป้องกันรอบที่สอง (จาก React StrictMode หรือ Hot Reload)
    isFetched.current = true;

    const loadAllData = async () => {
      setLoading(true);
      await Promise.all([refreshUserPortfolios(), refreshAdminPortfolios()]);
      setLoading(false);
    };
    loadAllData();
  }, [displayMode]);

  // --- ฟังก์ชันจัดการ Modal ---
  const openTransactionModal = (params = {}) => {
    setTxModalParams(params);
    setIsTxModalOpen(true);
  };

  const closeTransactionModal = () => {
    setIsTxModalOpen(false);
    setTxModalParams({});
  };

  const handleConfirmTransaction = async (portfolioId: string, subPortfolioId: string, transactions: any[]) => {
    try {
      // 1. เตรียมข้อมูล
      const formattedItems = transactions.map(t => ({
        type: t.type.toUpperCase(),
        subPortId: t.subPortId,
        assetSymbol: t.symbol,
        quantity: Number(t.quantity),
        price: Number(t.price),
        fee: Number(t.fee) || 0,
        tax: Number(t.tax) || 0,
        idempotencyKey: t.idempotencyKey,
        transactionDate: t.date ? new Date(t.date).toISOString() : new Date().toISOString()
      }));

      await createV2BulkTransactions(portfolioId, formattedItems);
     

      await refreshUserPortfolios();

      // ✅ 3. เพิ่มบรรทัดนี้เข้าไป! (เพื่อส่งสัญญาณว่า "เฮ้ย ข้อมูลเปลี่ยนแล้วนะ!")
      setGlobalRefreshCount(prev => prev + 1);
    

    } catch (error: any) {
      console.error(error);
      // โยน error ต่อให้ SellBuyStockModal แสดง toast (พร้อม Trace ID) และไม่เปิด popup ทำรายการสำเร็จ
      throw error;
    }
  };

  // Modal ซื้อขายหุ้นใช้ตัวเลขแบบ Number จึงทำรายการกับพอร์ตคริปโตไม่ได้
  // แต่ทั้งสอง Modal โชว์พอร์ตครบทุกชนิด — เลือกพอร์ตอีกชนิดแล้วสลับไป Modal ที่ถูกต้อง (ล้าง params เดิม เช่น symbol/ราคา)
  const stockPortfolios = userPortfolios.filter(p => !isCryptoPortfolio(p));
  const cryptoPortfolios = userPortfolios.filter(p => isCryptoPortfolio(p));
  const txCryptoPortfolio = txModalParams.portId
    ? userPortfolios.find(p => (p.portfolioId || p.portId || p.id) === txModalParams.portId && isCryptoPortfolio(p))
    : undefined;

  return (
    <PortfolioContext.Provider
      value={{
        userPortfolios,
        adminPortfolios,
        loading,
        refreshUserPortfolios,
        refreshAdminPortfolios,
        openTransactionModal,
        globalRefreshCount,
        displayMode,
        setDisplayMode,
        toggleDisplayMode,
        shayPortfolios, // ✅ ส่งค่าออกไป
        refreshShayPortfolios, // ✅ ส่งฟังก์ชันออกไป
      }}
    >
      {children}

      {/* 2. ฝัง Modal ไว้ตรงนี้เลย! มันจะทำงานซ้อนทับทุกหน้า */}
      {isTxModalOpen && txCryptoPortfolio && (
        <CryptoTradeModal
          isOpen={isTxModalOpen}
          onClose={closeTransactionModal}
          portfolios={cryptoPortfolios}
          stockPortfolios={stockPortfolios}
          onSwitchToStock={(portId) => setTxModalParams({ portId })}
          initialPortfolioId={txModalParams.portId}
          initialSubPortId={txModalParams.subPortId}
          initialSymbol={txModalParams.symbol}
          isSellAll={txModalParams.isSellAll}
          onDone={async () => {
            await refreshUserPortfolios();
            setGlobalRefreshCount(prev => prev + 1);
          }}
        />
      )}
      {isTxModalOpen && !txCryptoPortfolio && (
        <SellBuyStockModal
          isOpen={isTxModalOpen}
          onClose={closeTransactionModal}
          portfolios={stockPortfolios}
          cryptoPortfolios={cryptoPortfolios}
          onSwitchToCrypto={(portId) => setTxModalParams({ portId })}
          initialPortfolioId={txModalParams.portId}
          initialSubPortfolioId={txModalParams.subPortId}
          initialSymbol={txModalParams.symbol}        // ✅ ต้องเพิ่มบรรทัดนี้!
          initialPrice={txModalParams.currentPrice}
          onConfirm={handleConfirmTransaction}
          onAdjustCash={handleAdjustCash}
          onRefresh={refreshUserPortfolios}
          initialQuantity={txModalParams.initialQuantity} // ✅ ส่งต่อ
          isSellAll={txModalParams.isSellAll}             // ✅ ส่งต่อ
        />
      )}
    </PortfolioContext.Provider>
  );
};

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (!context) throw new Error('usePortfolio must be used within a PortfolioProvider');
  return context;
};